(function () {
  const PRODUCT_PARAM = "products";
  const PRODUCT_CARD_SECTION_ID = "adp-product-card";
  const PREVIEW_PARAMS = ["preview_theme_id", "_ab", "_fd", "_sc", "pb"];
  const productCardCache = new Map();
  let isApplying = false;
  let observer = null;
  let observerTimer = null;

  function escapeSelector(value) {
    if (window.CSS?.escape) {
      return CSS.escape(value);
    }

    return value.replace(/["\\]/g, "\\$&");
  }

  function copyPreviewParams(toUrl, fromUrl = new URL(window.location.href)) {
    PREVIEW_PARAMS.forEach((paramName) => {
      const paramValue = fromUrl.searchParams.get(paramName);

      if (paramValue !== null && !toUrl.searchParams.has(paramName)) {
        toUrl.searchParams.set(paramName, paramValue);
      }
    });
  }

  function getAdpHandles() {
    const rawProducts = new URLSearchParams(window.location.search).get(PRODUCT_PARAM);

    if (!rawProducts) {
      return [];
    }

    return rawProducts
      .split(",")
      .map((handle) => handle.trim().toLowerCase())
      .filter((handle) => /^[a-z0-9][a-z0-9-]*$/.test(handle))
      .filter((handle, index, handles) => handles.indexOf(handle) === index);
  }

  function getMainCollectionSection() {
    return document.querySelector(".shopify-section--main-collection");
  }

  function getProductList() {
    return getMainCollectionSection()?.querySelector("product-list.product-list");
  }

  function getProductCards(productList) {
    return Array.from(productList.children).filter((child) => child.tagName === "PRODUCT-CARD");
  }

  function findProductCard(productList, handle) {
    return getProductCards(productList).find((card) => card.getAttribute("handle") === handle) || null;
  }

  async function fetchSection(url) {
    const response = await fetch(url.toString(), {
      headers: {
        "X-Requested-With": "XMLHttpRequest"
      }
    });

    if (!response.ok) {
      throw new Error(`ADP section request failed: ${response.status}`);
    }

    return new DOMParser().parseFromString(await response.text(), "text/html");
  }

  function rememberPageSize(productList) {
    if (!productList.dataset.adpPageSize) {
      productList.dataset.adpPageSize = String(getProductCards(productList).length);
    }
  }

  async function fetchProductCard(handle) {
    if (productCardCache.has(handle)) {
      return productCardCache.get(handle).cloneNode(true);
    }

    try {
      const root = window.Shopify?.routes?.root || "/";
      const productUrl = new URL(`${root}products/${encodeURIComponent(handle)}`, window.location.origin);
      copyPreviewParams(productUrl);
      productUrl.searchParams.set("section_id", PRODUCT_CARD_SECTION_ID);

      const productDocument = await fetchSection(productUrl);
      const card = productDocument.querySelector(`product-card[handle="${escapeSelector(handle)}"]`);

      if (!card) {
        console.warn(`[ADP] Product card was not found for handle: ${handle}`);
        return null;
      }

      productCardCache.set(handle, card);
      return card.cloneNode(true);
    } catch (error) {
      console.warn(`[ADP] Unable to fetch product card for handle: ${handle}`, error);
      return null;
    }
  }

  async function getPromotedCards(productList, handles) {
    const promotedCards = [];

    for (const handle of handles) {
      let card = findProductCard(productList, handle);

      if (!card) {
        card = await fetchProductCard(handle);
      }

      if (!card) {
        continue;
      }

      card.dataset.adpPromoted = "true";
      promotedCards.push(card);
    }

    return promotedCards;
  }

  function removeDuplicatePromotedCards(productList, promotedCards) {
    const promotedHandles = new Set(promotedCards.map((card) => card.getAttribute("handle")));

    getProductCards(productList).forEach((card) => {
      const handle = card.getAttribute("handle");

      if (promotedHandles.has(handle) && !promotedCards.includes(card)) {
        card.remove();
      }
    });
  }

  function trimToPageSize(productList) {
    const pageSize = Number(productList.dataset.adpPageSize);

    if (!pageSize) {
      return;
    }

    while (getProductCards(productList).length > pageSize) {
      const removableCards = getProductCards(productList).filter((card) => card.dataset.adpPromoted !== "true");
      const cardToRemove = removableCards[removableCards.length - 1];

      if (!cardToRemove) {
        break;
      }

      cardToRemove.remove();
    }
  }

  function refreshReviewWidgets() {
    window.jdgm?.customizeBadges?.();
  }

  async function applyAdp() {
    const handles = getAdpHandles();

    if (!handles.length || isApplying) {
      return;
    }

    isApplying = true;

    try {
      const productList = getProductList();

      if (!productList) {
        return;
      }

      rememberPageSize(productList);

      const promotedCards = await getPromotedCards(productList, handles);

      if (!promotedCards.length) {
        return;
      }

      const currentCards = getProductCards(productList);
      const alreadyPromoted = promotedCards.every((card, index) => currentCards[index] === card);

      if (alreadyPromoted) {
        trimToPageSize(productList);
        refreshReviewWidgets();
        return;
      }

      removeDuplicatePromotedCards(productList, promotedCards);
      productList.prepend(...promotedCards);
      trimToPageSize(productList);
      refreshReviewWidgets();
    } catch (error) {
      console.warn("[ADP] Unable to apply product ordering", error);
    } finally {
      isApplying = false;
    }
  }

  function scheduleApplyAdp() {
    window.clearTimeout(observerTimer);
    observerTimer = window.setTimeout(applyAdp, 80);
  }

  function preserveAdpForFacetRequest(event) {
    const handles = getAdpHandles();
    const url = event.detail?.url;

    if (!handles.length || !(url instanceof URL)) {
      return;
    }

    copyPreviewParams(url);
    url.searchParams.set(PRODUCT_PARAM, handles.join(","));
  }

  function initObserver() {
    const sectionElement = getMainCollectionSection();

    if (!sectionElement || observer) {
      return;
    }

    observer = new MutationObserver(scheduleApplyAdp);
    observer.observe(sectionElement, { childList: true, subtree: true });
  }

  function initAdp() {
    initObserver();
    applyAdp();
  }

  document.addEventListener("facet:update", preserveAdpForFacetRequest, true);

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAdp);
  } else {
    initAdp();
  }

  window.addEventListener("pageshow", applyAdp);
})();